import React from 'react';
import { Text, View, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import SheetModal from '@/components/General/SheetModal';
import Unicon from '@/components/General/Unicon';
const FIELD_TYPES = [
    { type: 'text', label: 'Short Text', icon: 'text-outline', description: 'Single-line text input' },
    { type: 'longtext', label: 'Long Text', icon: 'document-text-outline', description: 'Multi-line text area' },
    { type: 'number', label: 'Number', icon: 'calculator-outline', description: 'Numeric input with min/max' },
    { type: 'date', label: 'Date', icon: 'calendar-outline', description: 'Date picker' },
    { type: 'time', label: 'Time', icon: 'time-outline', description: 'Time picker' },
    { type: 'dropdown', label: 'Dropdown', icon: 'chevron-down-circle-outline', description: 'Single-select from options' },
    { type: 'checkbox', label: 'Checkbox', icon: 'checkbox-outline', description: 'Single on/off toggle' },
    { type: 'checkboxGroup', label: 'Checkbox Group', icon: 'list-outline', description: 'Multi-select from options' },
    { type: 'file', label: 'File Upload', icon: 'cloud-upload-outline', description: 'Upload a file' },
    { type: 'email', label: 'Email', icon: 'mail-outline', description: 'Validated email input' },
    { type: 'phone', label: 'Phone', icon: 'call-outline', description: 'US phone number' },
    { type: 'address', label: 'Address', icon: 'location-outline', description: 'Street, city, state, zip' },
    { type: 'name', label: 'Name', icon: 'person-outline', description: 'First and last name' },
    { type: 'payment', label: 'Payment', icon: 'card-outline', description: 'Fixed-price payment into a fund' },
    { type: 'signature', label: 'Signature', icon: 'create-outline', description: 'Drawn signature with consent' },
    { type: 'profile', label: 'Profile', icon: 'person-circle-outline', description: 'Link an existing person profile' },
];

// Profile fields need an authenticated directory picker — they can never be
// offered on public forms. Signatures ARE allowed: anonymous signers enter
// their own name/capacity/consent/ink — no directory access required.
const PUBLIC_DISABLED_TYPES = new Set(['profile']);
const PUBLIC_DISABLED_REASON = 'Not available on public forms.';

const FieldTypePicker = ({ isOpen, onClose, onPick, paymentDisabled = false, isPublic = false }) => {
    return (
        <SheetModal
            title="Add Field"
            helpText="Choose a field type to add to this section."
            isOpen={isOpen}
            onCancel={onClose}
        >
            <View style={styles.grid}>
                {FIELD_TYPES.map((ft) => {
                    const disabledOption = (ft.type === 'payment' && paymentDisabled)
                        || (isPublic && PUBLIC_DISABLED_TYPES.has(ft.type));
                    return (
                        <Pressable
                            key={ft.type}
                            style={[styles.option, disabledOption && { opacity: 0.4 }]}
                            disabled={disabledOption}
                            testID={`field-type-${ft.type}`}
                            onPress={() => {
                                onPick(ft.type);
                                onClose();
                            }}
                        >
                            <View style={styles.optionIcon}>
                                <Unicon name={ft.icon} size={22} colorVariant="forms" />
                            </View>
                            <View style={styles.optionText}>
                                <Text style={styles.optionLabel}>{ft.label}</Text>
                                <Text style={styles.optionDescription}>
                                    {disabledOption && isPublic && PUBLIC_DISABLED_TYPES.has(ft.type)
                                        ? PUBLIC_DISABLED_REASON
                                        : ft.description}
                                </Text>
                            </View>
                        </Pressable>
                    );
                })}
            </View>
        </SheetModal>
    );
};

export { FIELD_TYPES };
export default FieldTypePicker;

const styles = StyleSheet.create((theme) => ({
    grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, paddingHorizontal: 8 },
    option: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        padding: 10,
        marginBottom: 6,
        borderRadius: 10,
        backgroundColor: theme.colors.surface,
        boxShadow: theme.shadows.card,
        width: '100%',
        _web: {
            width: 'calc(50% - 4px)',
            transition: "all 0.15s ease",
            _hover: { backgroundColor: theme.colors.hover, transform: [{ scale: 1.01 }], boxShadow: theme.shadows.cardHover },
        },
    },
    optionIcon: {
        width: 40,
        height: 40,
        borderRadius: 10,
        backgroundColor: theme.colors.formsContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    optionText: { flex: 1 },
    optionLabel: {
        fontSize: 15,
        fontFamily: theme.fonts.heading,
        color: theme.colors.text,
    },
    optionDescription: {
        fontSize: 12,
        color: theme.colors.textSecondary,
        marginTop: 1,
    },
}));
